import React, { useCallback, useMemo } from 'react';
import { Drawer } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { FilterEmploymentType } from 'shared/components/filters/inputs/employment-type/filter-employment-type';
import {
  ProjectPlanningFiltersFormType,
  ProjectPlanningFiltersProps,
} from './project-planning-filters.types';
import { getProjectPlanningFiltersDefaultValues } from './utils/get-project-planning-default-values';
import { FilterWrapper } from '../../../../../../shared/components/filters/filter-wrapper';
import { FilterBusinessUnitsWithCompany } from '../../../../../../shared/components/filters/inputs/business-units-with-company/filter-business-units-with-company';
import { FilterFilledStatus } from '../../../../../../shared/components/filters/inputs/filled-status/filter-filled-status';
import { FilterJobTypes } from '../../../../../../shared/components/filters/inputs/job-types/filter-job-types';
import { FilterToDate } from '../../../../../../shared/components/filters/inputs/date/filter-to-date';
import { FilterApplicantStatus } from '../../../../../../shared/components/forms/inputs/filter-applicant-status/filter-applicant-status';

export const ProjectPlanningFilters = ({
  toggleFilters,
  handleFiltersChange,
  appliedFilters,
  isOpen,
  filterData,
}: ProjectPlanningFiltersProps) => {
  const defaultFilterValues = useMemo(() => {
    return getProjectPlanningFiltersDefaultValues(appliedFilters, filterData);
  }, [appliedFilters, filterData]);

  const form = useForm<ProjectPlanningFiltersFormType>({
    defaultValues: { ...defaultFilterValues },
  });

  const handleClear = useCallback(() => {
    form.reset({
      toDate: defaultFilterValues.toDate,
      toDateCheckboxValue: false,
      businessUnitCheckboxValue: false,
      company: null,
      branchOffices: null,
      departments: null,
      filledStatusCheckboxValue: false,
      filledStatus: null,
      jobTypeCheckboxValue: false,
      jobType: [],
      employmentTypeCheckboxValue: false,
      isFreelance: false,
      isTempWork: false,
      areFreelanceApplications: false,
      areTempworkApplications: false,
      areNoApplications: false,
      applicationTypesCheckboxValue: false,
    });
  }, [defaultFilterValues, form]);

  const handleCancel = useCallback(() => {
    toggleFilters();
  }, [toggleFilters]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      form.handleSubmit((data) => {
        const branchOffices = data.branchOffices?.map((item) => item.value) ?? undefined;
        const departments = data.departments?.map((item) => item.value) ?? undefined;

        const dataToSubmit = {
          toDate:
            data.toDateCheckboxValue && data.toDate
              ? DateTime.fromFormat(data.toDate, 'yyyy-MM-dd')
              : undefined,
          company: data.businessUnitCheckboxValue ? data.company?.value : undefined,
          branchOffices: data.businessUnitCheckboxValue ? branchOffices : undefined,
          filledStatus:
            data.filledStatusCheckboxValue && data.filledStatus !== null
              ? data.filledStatus
              : undefined,
          jobType:
            data.jobTypeCheckboxValue && data.jobType?.length
              ? data.jobType.map((jobTypeSingle) => jobTypeSingle.id)
              : undefined,
          departments: data.businessUnitCheckboxValue ? departments : undefined,
          isFreelance: data.employmentTypeCheckboxValue ? data.isFreelance : undefined,
          isTempWork: data.employmentTypeCheckboxValue ? data.isTempWork : undefined,
          areFreelanceApplications: data.applicationTypesCheckboxValue
            ? data.areFreelanceApplications
            : undefined,
          areTempworkApplications: data.applicationTypesCheckboxValue
            ? data.areTempworkApplications
            : undefined,
          areNoApplications: data.applicationTypesCheckboxValue
            ? data.areNoApplications
            : undefined,
        };

        handleFiltersChange(dataToSubmit);
        toggleFilters();
      })();
    },
    [form, handleFiltersChange, toggleFilters]
  );

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleCancel}>
      <FormProvider {...form}>
        <FilterWrapper
          filterName="Filter"
          onCancel={handleCancel}
          onClear={handleClear}
          onSubmit={handleSubmit}
        >
          <FilterToDate />
          <FilterBusinessUnitsWithCompany companies={filterData.companies} />
          <FilterApplicantStatus />
          <FilterFilledStatus />
          <FilterJobTypes jobTypes={filterData.jobTypes} />
          <FilterEmploymentType />
        </FilterWrapper>
      </FormProvider>
    </Drawer>
  );
};
